import React, { useState, useEffect } from "react";
import { RefreshCw, Bot, ShoppingCart, Ban, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDate } from "../../lib/api";
import AutoLogTable from "./AutoLogTable";
import { HandlingDialog, HandlingDetails } from "./HandlingDialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { DataTable, platformBadge, keepStatusBadge, PLATFORM_FILTER } from "./shared";

const cashierBadge = (s) => (s === "converted"
  ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Jadi Sales</Badge>
  : s === "skipped_returned"
  ? <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">Dilewati (Retur)</Badge>
  : s === "skipped_duplicate"
  ? <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">Dilewati (Dobel Kasir)</Badge>
  : <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Gagal</Badge>);
const cancelBadge = (s) => (s === "cancelled"
  ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Keep dibatalkan</Badge>
  : s === "in_sale" ? <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Sudah jadi Sales</Badge>
  : <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Gagal</Badge>);
const reprocessBadge = (s) => (s === "recovered"
  ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Keep dipulihkan</Badge>
  : <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Gagal</Badge>);

const statusFilter = (opts) => ({ key: "status", label: "Status", allLabel: "Semua status", options: opts, match: (r, v) => r.status === v });

const dateColumn = (key, header, prefix) => ({
  key, header, sortAccessor: (r) => r[key] || 0,
  render: (r) => <span className="text-xs" data-testid={`${prefix}-date-${r.order_sn}`}>{r[key] ? fmtDate(r[key]) : "Belum tersedia"}</span>,
});
const keepCols = [
  { key: "order_sn", header: "Order ID", className: "font-mono text-xs", sortAccessor: (r) => r.order_sn },
  dateColumn("order_created_time", "Tanggal Pesanan", "ak"),
  { key: "platform", header: "Platform", render: (r) => platformBadge(r.platform) },
  { key: "status", header: "Status / Penanganan", render: (r) => <div className="space-y-2">
    {keepStatusBadge(r.status)}
    {r.status === "resolved" && <HandlingDetails row={r} kind="keep" />}
  </div> },
  { key: "keep_numbers", header: "No Keep", className: "text-sm", render: (r) => (r.keep_numbers || []).join(", ") || "-" },
  { key: "reason", header: "Alasan", className: "text-xs text-red-600 max-w-md", render: (r) => r.reason || "-" },
  { key: "attempts", header: "Percobaan", align: "right", sortAccessor: (r) => r.attempts || 1, render: (r) => r.attempts || 1 },
];
const cashierCols = [
  { key: "order_sn", header: "Order ID", className: "font-mono text-xs", sortAccessor: (r) => r.order_sn },
  dateColumn("desty_updated_time", "Update Terakhir Desty", "ak-cashier"),
  { key: "platform", header: "Platform", render: (r) => platformBadge(r.platform) },
  { key: "status", header: "Status", render: (r) => cashierBadge(r.status) },
  { key: "sale_number", header: "No Sales", className: "text-sm", render: (r) => r.sale_number || "-" },
  { key: "items", header: "Item Bermasalah", className: "text-xs max-w-sm", render: (r) => {
      const its = (r.items || []).filter((x) => x.issue && x.issue !== "ok");
      if (!its.length) return <span className="text-cocoa/40">-</span>;
      return (
        <div className="space-y-1">
          {its.map((x, i) => (
            <div key={i} className="flex items-center gap-1.5 flex-wrap">
              <span className="font-mono text-cocoa">{x.sku || x.name || "?"}</span>
              <span className="text-cocoa/50">×{x.qty}</span>
              {x.issue === "unmapped"
                ? <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Belum mapping</Badge>
                : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Stok {x.available ?? 0}</Badge>}
            </div>
          ))}
        </div>
      );
    } },
  { key: "reason", header: "Alasan", className: "text-xs text-red-600 max-w-xs", render: (r) => r.reason || "-" },
];
const cancelCols = [
  { key: "order_sn", header: "Order ID", className: "font-mono text-xs", sortAccessor: (r) => r.order_sn },
  { key: "platform", header: "Platform", render: (r) => platformBadge(r.platform) },
  { key: "status", header: "Status", render: (r) => cancelBadge(r.status) },
  { key: "handled", header: "Penanganan", render: (r) => (
    <div className="space-y-1" data-testid={`ak-cancel-handled-${r.order_sn}`}>
      <Badge className={r.handled ? "bg-sky-100 text-sky-700 hover:bg-sky-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}>
        {r.handled ? "Sudah ditangani" : "Belum ditangani"}
      </Badge>
      {r.handled && <HandlingDetails row={r} kind="cancel" />}
    </div>
  ) },
  { key: "ref", header: "No Keep / Sales", className: "text-sm", render: (r) => r.keep_number || r.sale_number || "-" },
  { key: "reason", header: "Alasan", className: "text-xs text-cocoa/70 max-w-md", render: (r) => r.reason || "-" },
];
const reprocessCols = [
  { key: "order_sn", header: "Order ID", className: "font-mono text-xs", sortAccessor: (r) => r.order_sn },
  { key: "platform", header: "Platform", render: (r) => platformBadge(r.platform) },
  { key: "status", header: "Status", render: (r) => reprocessBadge(r.status) },
  { key: "keep_numbers", header: "No Keep", className: "text-sm", render: (r) => (r.keep_numbers || []).join(", ") || "-" },
  { key: "reason", header: "Alasan", className: "text-xs text-red-600 max-w-md", render: (r) => r.reason || "-" },
  { key: "attempts", header: "Percobaan", align: "right", sortAccessor: (r) => r.attempts || 1, render: (r) => r.attempts || 1 },
];

export default function AutoKeepLogs({ write, reloadSignal }) {
  const [logReload, setLogReload] = useState(0);
  const [resolving, setResolving] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [cashierReload, setCashierReload] = useState(0);
  const [cancelReload, setCancelReload] = useState(0);
  const [handlingCancel, setHandlingCancel] = useState("");
  const [handlingTarget, setHandlingTarget] = useState(null);
  const [reprocessLog, setReprocessLog] = useState([]);
  const [migCount, setMigCount] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [reproSns, setReproSns] = useState("");
  const [reproing, setReproing] = useState(false);

  const reprocess = async () => {
    const sns = reproSns.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    if (!sns.length) return toast.error("Isi minimal 1 Order ID");
    setReproing(true);
    try {
      const res = await api.post("/desty/reprocess-orders", { order_sns: sns });
      const rows = res.data.processed || [];
      const okk = rows.filter((p) => p.status === "created").length;
      toast.success(`Proses ulang selesai: keep dibuat ${okk}/${rows.length}`, { description: rows.map((p) => `${p.order_sn}: ${p.status}${p.reason ? " (" + p.reason + ")" : ""}`).join(" | ") });
      setReproSns(""); loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setReproing(false); }
  };

  const fixPremature = async () => {
    setFixing(true);
    try {
      const dry = await api.post("/desty/revert-premature-sales", { apply: false });
      if (!dry.data.candidates) { toast.info("Tidak ada Sales prematur — semua sudah benar dikirim."); return; }
      if (!window.confirm(`${dry.data.candidates} Sales dari order yang BELUM dikirim akan dibalikkan ke Keep. Lanjutkan?`)) return;
      const res = await api.post("/desty/revert-premature-sales", { apply: true });
      toast.success(`${res.data.reverted} Sales dibalikkan ke Keep`);
      if ((res.data.errors || []).length) toast.error(`${res.data.errors.length} gagal — cek konsol`, { description: res.data.errors.join(" | ") });
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setFixing(false); }
  };

  const [cleaningKilat, setCleaningKilat] = useState(false);
  const runKilatCleanJob = async (apply) => {
    const { data } = await api.post("/desty/clean-kilat-duplicate-sales", { apply });
    for (let i = 0; i < 90; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      const st = await api.get(`/desty/clean-kilat-duplicate-sales/${data.job_id}`);
      if (st.data.status === "done") return st.data.result;
      if (st.data.status === "error") throw new Error(st.data.error);
    }
    throw new Error("Pemindaian Desty terlalu lama, coba lagi.");
  };
  const cleanKilatDup = async () => {
    setCleaningKilat(true);
    try {
      const dry = await runKilatCleanJob(false);
      if (!dry.candidates) { toast.info("Tidak ada Sales Kilat dobel."); return; }
      const list = dry.preview.slice(0, 15).map((p) => `${p.sale_number} (${p.order_id}, ${p.qty} pcs)`).join("\n");
      if (!window.confirm(`${dry.candidates} Sales ternyata pesanan Kilat (stok sudah tercatat di reservasi Kilat).\nStok akan dikembalikan, Keep dibatalkan, dan Sales dihapus:\n\n${list}${dry.candidates > 15 ? "\n…" : ""}\n\nLanjutkan?`)) return;
      const res = await runKilatCleanJob(true);
      toast.success(`${res.reverted} Sales Kilat dobel dibersihkan`);
      if ((res.errors || []).length) toast.error(`${res.errors.length} gagal`, { description: res.errors.join(" | ") });
      loadAll();
    } catch (e) { toast.error(e.response ? formatApiError(e.response?.data?.detail) : e.message); }
    finally { setCleaningKilat(false); }
  };

  const doResolve = (r) => setHandlingTarget({ kind: "keep", orderSns: [r.order_sn], bulk: false });

  const submitHandling = async (details) => {
    const { kind, orderSns, bulk } = handlingTarget;
    if (kind === "cancel") {
      await api.post("/desty/autocancel-log/handled", { order_sn: orderSns[0], handled: true, ...details });
      toast.success("Penanganan Auto Cancel tersimpan");
      loadCancelLog();
    } else if (bulk) {
      const res = await api.post("/desty/autokeep-log/resolve-bulk", { order_sns: orderSns, ...details });
      toast.success(`${res.data.resolved} order ditandai sudah ditangani`);
      setSelected(new Set()); loadLog();
    } else {
      await api.post("/desty/autokeep-log/resolve", { order_sn: orderSns[0], ...details });
      toast.success(`Penanganan order ${orderSns[0]} tersimpan`);
      setSelected((prev) => { const next = new Set(prev); next.delete(orderSns[0]); return next; });
      loadLog();
    }
    setHandlingTarget(null);
  };

  const doUnresolve = async (r) => {
    if (!window.confirm(`Kembalikan order ${r.order_sn} ke daftar GAGAL?`)) return;
    setResolving(r.order_sn);
    try {
      await api.post("/desty/autokeep-log/unresolve", { order_sn: r.order_sn });
      toast.success(`Order ${r.order_sn} dikembalikan ke Gagal`);
      loadLog();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setResolving(""); }
  };

  const toggleSel = (sn) => setSelected((prev) => { const n = new Set(prev); n.has(sn) ? n.delete(sn) : n.add(sn); return n; });

  const doResolveBulk = () => {
    const sns = [...selected];
    if (!sns.length) return toast.error("Pilih minimal 1 order gagal");
    setHandlingTarget({ kind: "keep", orderSns: sns, bulk: true });
  };

  const loadLog = () => setLogReload((v) => v + 1);
  const loadCashierLog = () => setCashierReload((v) => v + 1);
  const loadCancelLog = () => setCancelReload((v) => v + 1);
  const doUnhandleCancel = async (r) => {
    if (!window.confirm(`Batalkan tanda penanganan untuk order ${r.order_sn}?\n\nIni hanya penanda log, tidak mengubah stok, Keep, Sales, atau proses Auto Cancel.`)) return;
    setHandlingCancel(r.order_sn);
    try {
      await api.post("/desty/autocancel-log/handled", { order_sn: r.order_sn, handled: false });
      toast.success("Log dikembalikan ke belum ditangani");
      loadCancelLog();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setHandlingCancel(""); }
  };
  const loadReprocessLog = () => api.get("/desty/autoreprocess-log").then((r) => setReprocessLog(r.data.rows || [])).catch(() => {});
  const loadAll = () => { loadLog(); loadCashierLog(); loadCancelLog(); loadReprocessLog(); };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (reloadSignal) loadAll(); }, [reloadSignal]);

  const dryRunMig = async () => {
    setMigrating(true);
    try { const r = await api.post("/desty/migrate-phone-orderid", { apply: false }); setMigCount(r.data.candidates); toast.info(`${r.data.candidates} keep bisa dirapikan (phone → Order ID)`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setMigrating(false); }
  };
  const applyMig = async () => {
    setMigrating(true);
    try { const r = await api.post("/desty/migrate-phone-orderid", { apply: true }); toast.success(`${r.data.migrated} keep dirapikan`); setMigCount(null); loadLog(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setMigrating(false); }
  };

  return (
    <>
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="font-semibold text-cocoa flex items-center gap-2"><Bot className="w-4 h-4" /> Log Auto Keep</div>
          <Button variant="ghost" size="sm" onClick={loadLog} data-testid="ak-refresh-log"><RefreshCw className="w-4 h-4" /></Button>
        </div>
        <AutoLogTable endpoint="/desty/autokeep-log" dateLabel="Tanggal Pesanan" reloadSignal={logReload} testid="ak-log-table" columns={write ? [
            { key: "_sel", header: "", render: (r) => (r.status === "failed"
              ? <input type="checkbox" className="accent-sky-600 w-4 h-4" checked={selected.has(r.order_sn)} onChange={() => toggleSel(r.order_sn)} data-testid={`ak-sel-${r.order_sn}`} />
              : null) },
            ...keepCols,
            { key: "_act", header: "Aksi", align: "right", render: (r) => (r.status === "failed"
              ? <Button size="sm" variant="outline" className="h-7 text-xs border-sky-500 text-sky-700 hover:bg-sky-50" disabled={resolving === r.order_sn} onClick={() => doResolve(r)} data-testid={`ak-resolve-${r.order_sn}`}>{resolving === r.order_sn ? "…" : "Sudah ditangani"}</Button>
              : r.status === "resolved"
              ? <Button size="sm" variant="ghost" className="h-7 text-xs text-cocoa/70 hover:text-cocoa" disabled={resolving === r.order_sn} onClick={() => doUnresolve(r)} data-testid={`ak-unresolve-${r.order_sn}`}>Kembalikan ke Gagal</Button>
              : <span className="text-cocoa/30">-</span>) },
          ] : keepCols}
          searchAccessors={[(r) => r.order_sn, (r) => (r.keep_numbers || []).join(","), (r) => r.reason]}
          searchPlaceholder="Cari Order ID / no keep / alasan…"
          filters={[PLATFORM_FILTER, statusFilter([{ value: "failed", label: "Gagal / Belum bisa" }, { value: "created", label: "Berhasil" }, { value: "resolved", label: "Sudah ditangani" }, { value: "cancelled", label: "Dibatalkan pembeli" }, { value: "skipped", label: "Skip" }])]}
          rightSlot={write && (
            <div className="flex items-center gap-2 flex-wrap">
              {selected.size > 0 && <Button onClick={doResolveBulk} disabled={resolving === "__bulk__"} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="ak-resolve-bulk">{resolving === "__bulk__" ? "Memproses…" : `Tandai Terpilih (${selected.size})`}</Button>}
              <Input value={reproSns} onChange={(e) => setReproSns(e.target.value)} placeholder="Order ID (pisah koma)" className="h-9 w-52" data-testid="ak-reprocess-input" />
              <Button variant="outline" onClick={reprocess} disabled={reproing} data-testid="ak-reprocess-btn">{reproing ? "Memproses…" : "Proses Ulang Keep"}</Button>
              {migCount === null
                ? <Button variant="outline" onClick={dryRunMig} disabled={migrating} data-testid="ak-mig-dry">Rapikan Data</Button>
                : <Button onClick={applyMig} disabled={migrating} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="ak-mig-apply">Terapkan ({migCount})</Button>}
            </div>)}
          emptyText="Belum ada data." />
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="font-semibold text-cocoa flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> Log Auto Kasir (Terkirim → Sales)</div>
          <Button variant="ghost" size="sm" onClick={loadCashierLog} data-testid="ak-refresh-cashierlog"><RefreshCw className="w-4 h-4" /></Button>
        </div>
        <AutoLogTable endpoint="/desty/autocashier-log" dateLabel="Update Terakhir Desty" reloadSignal={cashierReload} testid="ak-cashier-log-table" columns={cashierCols}
          searchAccessors={[(r) => r.order_sn, (r) => r.sale_number, (r) => r.reason, (r) => (r.items || []).map((x) => x.sku).join(",")]}
          searchPlaceholder="Cari Order ID / no sales / SKU / alasan…"
          filters={[PLATFORM_FILTER, statusFilter([{ value: "converted", label: "Jadi Sales" }, { value: "failed", label: "Gagal" }, { value: "skipped_returned", label: "Dilewati (Retur)" }, { value: "skipped_duplicate", label: "Dilewati (Dobel Kasir)" }])]}
          rightSlot={write && <div className="flex gap-2">
            <Button variant="outline" onClick={fixPremature} disabled={fixing} data-testid="ak-fix-premature">{fixing ? "Memeriksa…" : "Perbaiki Sales Prematur"}</Button>
            <Button variant="outline" className="border-rose-400 text-rose-700 hover:bg-rose-50" onClick={cleanKilatDup} disabled={cleaningKilat} data-testid="ak-clean-kilat-dup">{cleaningKilat ? "Memeriksa…" : "Bersihkan Sales Kilat Dobel"}</Button>
          </div>}
          emptyText="Belum ada data." />
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="font-semibold text-cocoa flex items-center gap-2"><Ban className="w-4 h-4" /> Log Auto Cancel (Dibatalkan → Cancel Keep)</div>
          <Button variant="ghost" size="sm" onClick={loadCancelLog} data-testid="ak-refresh-cancellog"><RefreshCw className="w-4 h-4" /></Button>
        </div>
        <p className="text-xs text-cocoa/60" data-testid="ak-cancel-handled-note">Penanda tim terpisah dari hasil Auto Cancel; tidak membatalkan Sales atau mengubah stok.</p>
        <AutoLogTable endpoint="/desty/autocancel-log" reloadSignal={cancelReload} testid="ak-cancel-log-table" columns={write ? [...cancelCols, {
          key: "_act", header: "Aksi", render: (r) => <Button size="sm" variant="outline"
            disabled={handlingCancel === r.order_sn}
            onClick={() => r.handled ? doUnhandleCancel(r) : setHandlingTarget({ kind: "cancel", orderSns: [r.order_sn], bulk: false })}
            data-testid={`ak-cancel-handle-${r.order_sn}`}>
            {handlingCancel === r.order_sn ? "Menyimpan…" : r.handled ? "Batal tandai" : "Sudah ditangani"}
          </Button>,
        }] : cancelCols}
          searchAccessors={[(r) => r.order_sn, (r) => r.keep_number, (r) => r.sale_number, (r) => r.reason]}
          searchPlaceholder="Cari Order ID / no keep / no sales…"
          filters={[PLATFORM_FILTER, statusFilter([{ value: "cancelled", label: "Keep dibatalkan" }, { value: "in_sale", label: "Sudah jadi Sales" }, { value: "failed", label: "Gagal" }]),
            { key: "handled", label: "Penanganan", allLabel: "Semua penanganan", options: [{ value: "unhandled", label: "Belum ditangani" }, { value: "handled", label: "Sudah ditangani" }] }]}
          emptyText="Belum ada data." />
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="font-semibold text-cocoa flex items-center gap-2"><RotateCcw className="w-4 h-4" /> Log Auto Reprocess (Keep hilang → dipulihkan)</div>
          <Button variant="ghost" size="sm" onClick={loadReprocessLog} data-testid="ak-refresh-reprocesslog"><RefreshCw className="w-4 h-4" /></Button>
        </div>
        <DataTable mobileCards testid="ak-reprocess-log-table" columns={reprocessCols} rows={reprocessLog}
          searchAccessors={[(r) => r.order_sn, (r) => (r.keep_numbers || []).join(","), (r) => r.reason]}
          searchPlaceholder="Cari Order ID / no keep / alasan…"
          filters={[PLATFORM_FILTER, statusFilter([{ value: "recovered", label: "Keep dipulihkan" }, { value: "failed", label: "Gagal" }])]}
          emptyText="Belum ada data." />
      </section>

      {handlingTarget && (
        <HandlingDialog target={handlingTarget} onClose={() => setHandlingTarget(null)} onConfirm={submitHandling} />
      )}
    </>
  );
}
